/**
 * MessageInfoModal — "פרטי הודעה" panel.
 *
 * Owner-spec 2026-05-31 (Phase 3): the first of three wired actions on
 * the message dropdown. Shows everything we know about one chat message
 * — id, timestamps, sender, channel, media metadata, ack — in a small
 * floating modal. WhatsApp Web ish, scoped down to what messages.json
 * actually carries.
 *
 * Controlled by the parent: open when `msg` is non-null, closed via
 * onClose. No internal state.
 */

import React from 'react';

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
  if (isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const formatBytes = (n) => {
  if (!n || isNaN(n)) return '—';
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const ackLabel = (ack) => {
  if (ack === 0) return 'בהמתנה';
  if (ack === 1) return 'נשלח';
  if (ack === 2) return 'נמסר';
  if (ack === 3) return 'נצפה';
  return '—';
};

const Row = ({ label, value, mono }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      padding: '6px 0',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '13px',
    }}>
      <div style={{ minWidth: '110px', color: '#666' }}>{label}</div>
      <div style={{
        flex: 1,
        color: '#1a1a1a',
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: 'break-all',
      }}>
        {String(value)}
      </div>
    </div>
  );
};

const MessageInfoModal = ({ msg, onClose }) => {
  if (!msg) return null;
  const type = (msg.mediaType || msg.type || 'text').toLowerCase();

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{
        background: 'white',
        borderRadius: '8px',
        maxWidth: '460px',
        width: '90vw',
        maxHeight: '80vh',
        overflow: 'auto',
        padding: '20px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#1a1a1a' }}>פרטי הודעה</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#666',
            }}
            title="סגור"
          >
            ✕
          </button>
        </div>

        <Row label="כיוון" value={msg.fromMe ? 'יוצאת' : 'נכנסת'} />
        <Row label="שולח" value={msg.sentByName || msg.senderName} />
        <Row label="סוג" value={type} />
        <Row label="ערוץ" value={msg.channel} />
        <Row label="ספק" value={msg.provider} />
        <Row label="זמן" value={formatTimestamp(msg.timestamp)} />
        <Row label="סטטוס" value={msg.ack !== undefined ? `${ackLabel(msg.ack)} (${msg.ack})` : undefined} />
        <Row label="גוף" value={msg.body && msg.body.length > 200 ? msg.body.slice(0, 200) + '…' : msg.body} />
        {msg.caption && <Row label="כיתוב" value={msg.caption} />}

        {/* Media block */}
        {(msg.mediaUrl || msg.mediaType) && (
          <>
            <Row label="קובץ" value={msg.mediaFilename} />
            <Row label="MIME" value={msg.mediaMimeType} mono />
            <Row label="גודל" value={formatBytes(msg.mediaSize)} />
            <Row label="URL" value={msg.mediaUrl} mono />
          </>
        )}

        <Row label="ID" value={msg.id} mono />
        {msg.replyTo && msg.replyTo.id && (
          <Row label="תגובה ל-ID" value={msg.replyTo.id} mono />
        )}
      </div>
    </div>
  );
};

export default MessageInfoModal;
